// React
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
// Navigation
import { useNavigate, useParams } from "react-router-dom";
// FHIR
import { EpisodeOfCare, Reference } from "fhir/r4";
// Translation
import i18n from "i18next";
// HL7 front library
import { StatusTag, Title } from "@fyrstain/hl7-front-library";
// Component
import AcesoPage from "src/components/AcesoPage/AcesoPage";
// Services
import {
  formatDate,
  getCodeableConceptValue,
  getCodeableConceptValues,
  getIdentifierValues,
  getReferenceIdentifier,
  getReferencePath,
  getReferenceValue,
  getStatusFlavor,
} from "src/services/resourceDetails.utils"
// Style
import "./EpisodeOfCareDetailsPage.css";

const EpisodeOfCareDetailsPage: FunctionComponent = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();

  const [episode, setEpisode] =
    useState<EpisodeOfCare>();
  const [loading, setLoading] = useState(true);

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback((error?: unknown) => {
    console.error(
      "EpisodeOfCare details error:",
      error ?? "Network or FHIR error",
    );
  }, []);

  //////////////////////////////
  //          Request         //
  //////////////////////////////

  useEffect(() => {
    if (!resourceId) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    fetch(
      `${process.env.REACT_APP_FHIR_URL}/EpisodeOfCare/${resourceId}`,
      {
        signal: controller.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `FHIR request failed with status ${response.status}`,
          );
        }

        return response.json() as Promise<EpisodeOfCare>;
      })
      .then((data) => {
        setEpisode(data);
      })
      .catch((error: unknown) => {
        if (
          error instanceof DOMException &&
          error.name === "AbortError"
        ) {
          return;
        }

        onError(error);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [onError, resourceId]);

  //////////////////////////////
  //         Functions        //
  //////////////////////////////

  function renderStringList(values: string[]) {
    return (
      <ul className="resource-details-list">
        {values.map((value, index) => (
          <li key={`${value}-${index}`}>
            {value}
          </li>
        ))}
      </ul>
    );
  }

  function renderReferences(
    references?: Reference[],
  ) {
    if (!references?.length) {
      return (
        <span className="resource-details-value">
          {i18n.t("general.notavailable")}
        </span>
      );
    }

    return renderStringList(
      references.map((reference) =>
        getReferenceValue(reference),
      ),
    );
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <AcesoPage
      loading={loading}
      titleKey="pathway.tabs.stays"
      needsLogin={true}
    >
      {!episode ? (
        <div className="alert alert-info">
          {i18n.t("general.notavailable")}
        </div>
      ) : (
        <div className="resource-details-page">
          <div className="resource-details-toolbar">
            <button
              className="resource-details-back-button"
              onClick={() => navigate(-1)}
              type="button"
            >
              {i18n.t("button.backtolist")}
            </button>
          </div>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.patient",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.patient")}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(episode.patient)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.patientidentifier",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceIdentifier(
                      episode.patient,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.patientreference",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferencePath(episode.patient)}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.episodeofcare",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.status")}
                  </span>

                  <StatusTag
                    message={i18n.t(
                      `episodeofcare.status.${episode.status}`,
                      {
                        defaultValue: episode.status,
                      },
                    )}
                    flavor={getStatusFlavor(
                      episode.status,
                    )}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.serviceprovider",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(
                      episode.managingOrganization,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.startdate")}
                  </span>

                  <span className="resource-details-value">
                    {formatDate(episode.period?.start)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.enddate")}
                  </span>

                  <span className="resource-details-value">
                    {formatDate(episode.period?.end)}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.stayidentifiers",
                    )}
                  </span>

                  {renderStringList(
                    getIdentifierValues(
                      episode.identifier,
                    ),
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card resource-details-static-card">
                  <span className="resource-details-label">
                    {i18n.t("label.type")}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      episode.type,
                    ),
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.diagnosis",
              )}
            />

            <div className="row g-3">
              {episode.diagnosis?.length ? (
                episode.diagnosis.map(
                  (diagnosis, index) => (
                    <div
                      className="col-12"
                      key={index}
                    >
                      <div className="resource-details-card">
                        <span className="resource-details-label">
                          {i18n.t("label.diagnosis")}{" "}
                          {index + 1}
                        </span>

                        <div className="resource-details-value">
                          <div>
                            <strong>
                              {i18n.t(
                                "label.condition",
                              )}
                              :
                            </strong>{" "}
                            {getReferenceValue(
                              diagnosis.condition,
                            )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t("label.role")}:
                            </strong>{" "}
                            {getCodeableConceptValue(
                              diagnosis.role,
                            )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t("label.rank")}:
                            </strong>{" "}
                            {diagnosis.rank ??
                              i18n.t(
                                "general.notavailable",
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className="col-12">
                  <div className="resource-details-card">
                    <span className="resource-details-value">
                      {i18n.t("general.notavailable")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.references",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.caremanager")}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(
                      episode.careManager,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.team")}
                  </span>

                  {renderReferences(episode.team)}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.referralrequests",
                    )}
                  </span>

                  {renderReferences(
                    episode.referralRequest,
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.accounts")}
                  </span>

                  {renderReferences(episode.account)}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </AcesoPage>
  );
};

export default EpisodeOfCareDetailsPage;
